import React, { useState } from 'react';
import { dataTypes } from '../utils/nodeTemplates';

/**
 * Editor for ProcessOutput list
 */
function OutputEditor({ outputs, onChange }) {
  const [expandedSchemas, setExpandedSchemas] = useState({});

  const toggleSchema = (index) => {
    setExpandedSchemas(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAdd = () => {
    const newOutput = {
      name: `output_${outputs.length + 1}`,
      type: 'string',
      description: '',
      extract_json_key: '',
      extract_pattern: '',
      json_schema: '',
    };
    onChange([...outputs, newOutput]);
  };

  const handleRemove = (index) => {
    const newOutputs = [...outputs];
    newOutputs.splice(index, 1);
    onChange(newOutputs);
  };

  const handleUpdate = (index, field, value) => {
    const newOutputs = [...outputs];
    newOutputs[index] = { ...newOutputs[index], [field]: value };
    onChange(newOutputs);
  };

  const validateJsonSchema = (value) => {
    if (!value || !value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="io-section">
      <div className="io-section-header">
        <h4>Outputs</h4>
        <button className="btn-small add" onClick={handleAdd}>+ Add</button>
      </div>

      {outputs.length === 0 ? (
        <p className="empty-message">No outputs defined</p>
      ) : (
        <div className="io-list">
          {outputs.map((output, index) => (
            <div key={index} className="io-item">
              <div className="io-item-header">
                <span className="io-item-name">
                  {output.name || `Output ${index + 1}`}
                </span>
                <div className="io-item-actions">
                  <button 
                    className="btn-icon delete" 
                    onClick={() => handleRemove(index)}
                    title="Remove output"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="io-item-row">
                <div className="io-item-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={output.name || ''}
                    onChange={(e) => handleUpdate(index, 'name', e.target.value)}
                    placeholder="output_name"
                  />
                </div>
                <div className="io-item-field">
                  <label>Type</label>
                  <select
                    value={output.type || 'string'}
                    onChange={(e) => handleUpdate(index, 'type', e.target.value)}
                  >
                    {dataTypes.map((dt) => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="io-item-row">
                <div className="io-item-field">
                  <label>Extract JSON Key</label>
                  <input
                    type="text"
                    value={output.extract_json_key || ''}
                    onChange={(e) => handleUpdate(index, 'extract_json_key', e.target.value)}
                    placeholder="key_name"
                  />
                </div>
              </div>

              <div className="io-item-row">
                <div className="io-item-field">
                  <label>Extract Pattern (regex)</label>
                  <input
                    type="text"
                    value={output.extract_pattern || ''}
                    onChange={(e) => handleUpdate(index, 'extract_pattern', e.target.value)}
                    placeholder="Optional regex pattern"
                  />
                </div>
              </div>

              <div className="io-item-row">
                <div className="io-item-field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={output.description || ''}
                    onChange={(e) => handleUpdate(index, 'description', e.target.value)}
                    placeholder="Output description"
                  />
                </div>
              </div>

              <div className="io-item-checkbox">
                <input
                  type="checkbox"
                  id={`passthrough-${index}`}
                  checked={output.passthrough || false}
                  onChange={(e) => handleUpdate(index, 'passthrough', e.target.checked)}
                />
                <label htmlFor={`passthrough-${index}`}>
                  Passthrough
                </label>
                <span className="help-text" style={{ marginLeft: 4 }}>
                  Copy value from input with the same name (bypasses LLM)
                </span>
              </div>

              {/* JSON Schema Section - for JSON type outputs */}
              {(output.type === 'json' || output.type === 'object' || output.type === 'array') && (
                <div className="io-item-row">
                  <div className="io-item-field full-width">
                    <div className="schema-header">
                      <label>JSON Schema</label>
                      <button 
                        type="button"
                        className="btn-link-small"
                        onClick={() => toggleSchema(index)}
                      >
                        {expandedSchemas[index] ? '▼ Collapse' : '▶ Expand'}
                      </button>
                    </div>
                    {expandedSchemas[index] && (
                      <>
                        <textarea
                          value={output.json_schema || ''}
                          onChange={(e) => handleUpdate(index, 'json_schema', e.target.value)}
                          placeholder={`{
  "type": "object",
  "properties": {
    "field_name": { "type": "string", "description": "Field description" }
  },
  "required": ["field_name"]
}`}
                          rows={8}
                          className={`code-textarea ${!validateJsonSchema(output.json_schema) ? 'invalid' : ''}`}
                        />
                        {!validateJsonSchema(output.json_schema) && (
                          <span className="error-hint">Invalid JSON format</span>
                        )}
                        <span className="help-text">
                          Define the expected JSON structure for LLM response parsing
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OutputEditor;
